import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Question, Answer, User } from '../models/models';
// SQLite-Import f�r Capacitor
import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';

export interface DBStatus {
  initialized: boolean;
  itemsCount: number;
  lastSyncTime?: number;
  lastError?: string;
}

export interface ProgressRecord {
  frage_id: number;
  user_id?: number;
  antwort?: string;
  ist_richtig?: number;
  zeit_sekunden?: number;
  updated_at?: string;
}

// SQLite-Row-Typ f�r Fragen
interface QuestionRow {
  frage_id: number;
  schein_id?: number | null;
  schein: string;
  kategorie: string;
  unterkategorie: string;
  buchseite: string;
  frage_text: string;
  korrekte_antwort: string;
  has_image: number;
  off_fragen_id?: string | null;
  antworten: string;
}

@Injectable({ providedIn: 'root' })
export class OfflineDbService {
  private db$ = new BehaviorSubject<any>(null);
  private dbStatus$ = new BehaviorSubject<DBStatus>({
    initialized: false,
    itemsCount: 0
  });
  private static readonly SCHEMA_VERSION = 2;
  private sqlite: SQLiteConnection | null = null;
  private db: SQLiteDBConnection | null = null;
  public dbReadyPromise: Promise<void>;
  private isNative: boolean;
  private dbMissingLogged = new Set<string>();

  private fallbackKey = 'offlineFallback';
  private fallbackStore = this.loadFallback();

  private logDbUnavailable(tag: string): void {
    if (!this.isNative) return;
    if (this.dbMissingLogged.has(tag)) return;
    this.dbMissingLogged.add(tag);
    console.log(`[SQLite] ${tag}: Keine DB-Verbindung!`);
  }

  private loadFallback() {
    try {
      const raw = localStorage.getItem(this.fallbackKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return { licenses: [], categories: {}, questions: {}, questionsById: {}, downloadedScheine: [], downloadedScheineIds: [] } as any;
  }

  private saveFallback() {
    try {
      localStorage.setItem(this.fallbackKey, JSON.stringify(this.fallbackStore));
    } catch (e) {
      console.warn('[SQLite] Fallback speichern fehlgeschlagen', e);
    }
  }

  constructor() {
    console.log('[SQLite] OfflineDbService Konstruktor gestartet');
    const hasWindow = typeof window !== 'undefined';
    this.isNative = hasWindow && typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.isNativePlatform && (window as any).Capacitor.isNativePlatform();
    if (this.isNative) {
      this.dbReadyPromise = this.initializeDatabase();
    } else {
      // Im Web: keine SQLite-Initialisierung, Promise sofort resolved
      this.dbReadyPromise = Promise.resolve();
      this.dbStatus$.next({ initialized: true, itemsCount: 0 });
    }
  }

  get isNativeApp(): boolean {
    return this.isNative;
  }

  async setSessionState(key: string, data: any): Promise<void> {
    if (!this.isNative || !this.db) return;
    const payload = JSON.stringify(data);
    const now = new Date().toISOString();
    await this.db.run('INSERT OR REPLACE INTO session_state (key, data, updated_at) VALUES (?, ?, ?)', [key, payload, now]);
  }

  async getSessionState<T = any>(key: string): Promise<T | null> {
    if (!this.isNative || !this.db) return null;
    const result = await this.db.query('SELECT data FROM session_state WHERE key = ?', [key]);
    const row = result.values?.[0];
    if (!row?.data) return null;
    try {
      return JSON.parse(row.data) as T;
    } catch (e) {
      console.warn('[SQLite] Failed to parse session_state', e);
      return null;
    }
  }

  async deleteSessionState(key: string): Promise<void> {
    if (!this.isNative || !this.db) return;
    await this.db.run('DELETE FROM session_state WHERE key = ?', [key]);
  }

  async setQuestionImage(frageId: number, data: string): Promise<void> {
    if (!this.isNative || !this.db) return;
    const now = new Date().toISOString();
    await this.db.run(
      'INSERT OR REPLACE INTO question_images (frage_id, data, updated_at) VALUES (?, ?, ?)',
      [frageId, data, now]
    );
  }

  async getQuestionImage(frageId: number): Promise<string | null> {
    if (!this.isNative || !this.db) return null;
    const res = await this.db.query('SELECT data FROM question_images WHERE frage_id = ?', [frageId]);
    const row = res.values?.[0];
    return row?.data || null;
  }

  async setAnswerImage(frageId: number, buchstabe: string, data: string): Promise<void> {
    if (!this.isNative || !this.db) return;
    const now = new Date().toISOString();
    await this.db.run(
      'INSERT OR REPLACE INTO answer_images (frage_id, buchstabe, data, updated_at) VALUES (?, ?, ?, ?)',
      [frageId, buchstabe, data, now]
    );
  }

  async getAnswerImage(frageId: number, buchstabe: string): Promise<string | null> {
    if (!this.isNative || !this.db) return null;
    const res = await this.db.query(
      'SELECT data FROM answer_images WHERE frage_id = ? AND buchstabe = ?',
      [frageId, buchstabe]
    );
    const row = res.values?.[0];
    return row?.data || null;
  }

  private async initializeDatabase(): Promise<void> {
    if (!this.isNative) {
      // Im Web: keine Initialisierung n�tig
      return;
    }
    try {
      console.log('[SQLite] Initialisiere Datenbank...');
      let isAvailable = true;
      try {
        const hasCheck = typeof (CapacitorSQLite as any)?.isAvailable === 'function';
        if (hasCheck) {
          const pluginAvailable = await (CapacitorSQLite as any).isAvailable();
          isAvailable = pluginAvailable === true || pluginAvailable?.result === true;
        } else {
          console.warn('[SQLite] isAvailable() nicht implementiert � versuche trotzdem zu initialisieren');
        }
      } catch (checkErr) {
        console.warn('[SQLite] isAvailable() Pr�fen fehlgeschlagen � versuche trotzdem zu initialisieren', checkErr);
        isAvailable = true;
      }
      if (!isAvailable) {
        console.warn('[SQLite] Plugin nicht verf�gbar � setze offline-DB auf Web-Modus');
        this.isNative = false;
        this.dbStatus$.next({ initialized: false, itemsCount: 0, lastError: 'SQLite Plugin nicht verf�gbar' });
        return;
      }

      const canCreate = typeof (CapacitorSQLite as any)?.createConnection === 'function';
      if (!canCreate) {
        console.warn('[SQLite] createConnection nicht verf�gbar � schalte auf Web-Modus');
        this.isNative = false;
        this.dbStatus$.next({ initialized: false, itemsCount: 0, lastError: 'SQLite Plugin nicht geladen' });
        return;
      }

      // Fallback: wenn oben auf Web-Modus geschaltet wurde, nicht weiter initialisieren
      if (!this.isNative) {
        this.dbStatus$.next({ initialized: true, itemsCount: 0 });
        return;
      }

      try {
        this.sqlite = new SQLiteConnection(CapacitorSQLite);
        this.db = await this.sqlite.createConnection('lernplattform', false, 'no-encryption', 1, false);
        await this.db.open();
        console.log('[SQLite] Verbindung ge?ffnet!');

        try {
          const versionRes = await this.db.query('PRAGMA user_version');
          const currentVersion = versionRes.values?.[0]?.user_version ?? 0;
          if (currentVersion < OfflineDbService.SCHEMA_VERSION) {
            console.warn(`[SQLite] Schema-Version alt (${currentVersion}) -> reset exam/offline Tabellen`);
            await this.db.execute('DROP TABLE IF EXISTS exam_boegen');
            await this.db.execute('DROP TABLE IF EXISTS downloaded_scheine');
            await this.db.execute('DROP TABLE IF EXISTS exam_results');
            await this.db.execute('DROP TABLE IF EXISTS question_images');
            await this.db.execute('DROP TABLE IF EXISTS answer_images');
            await this.db.execute(`PRAGMA user_version = ${OfflineDbService.SCHEMA_VERSION}`);
          } else if (currentVersion !== OfflineDbService.SCHEMA_VERSION) {
            await this.db.execute(`PRAGMA user_version = ${OfflineDbService.SCHEMA_VERSION}`);
          }
        } catch (verErr) {
          console.warn('[SQLite] Konnte user_version nicht pruefen/setzen', verErr);
        }
      } catch (connErr) {
        console.warn('[SQLite] createConnection/open fehlgeschlagen ? schalte auf Web-Modus', connErr);
        this.isNative = false;
        this.db = null;
        this.dbStatus$.next({ initialized: false, itemsCount: 0, lastError: 'SQLite Plugin nicht geladen' });
        return;
      }

      // EINMALIG: Alte Tabelle l�schen f�r Schema-Update
      // TODO: Nach dem ersten erfolgreichen Start wieder auskommentieren!
      //await this.db.execute('DROP TABLE IF EXISTS exam_boegen');
      //console.log('[SQLite] Alte exam_boegen Tabelle gel�scht f�r Schema-Update');

      // WICHTIG: Diese Zeile ist deaktiviert, damit die Daten persistent bleiben!
      // Nur bei Schema-�nderungen wieder aktivieren!
      // await this.db.execute('DROP TABLE IF EXISTS questions');

      // Korrekte Tabelle mit allen Spalten
      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS questions (
          frage_id INTEGER PRIMARY KEY,
          schein_id INTEGER,
          schein TEXT,
          kategorie TEXT,
          unterkategorie TEXT,
          buchseite TEXT,
          frage_text TEXT,
          korrekte_antwort TEXT,
          has_image INTEGER,
          off_fragen_id TEXT,
          antworten TEXT
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS progress (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          frage_id INTEGER,
          user_id INTEGER,
          antwort TEXT,
          ist_richtig INTEGER,
          zeit_sekunden INTEGER,
          updated_at TEXT
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS licenses (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          schein TEXT UNIQUE,
          name TEXT,
          beschreibung TEXT,
          schein_kurz TEXT,
          schein_lang TEXT,
          kategorie TEXT
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS downloaded_scheine (
          schein TEXT PRIMARY KEY,
          schein_id INTEGER,
          download_date TEXT,
          question_count INTEGER,
          is_complete INTEGER,
          last_updated TEXT,
          has_exam_content INTEGER,
          exam_bogen_count INTEGER,
          exam_last_updated TEXT
        )
      `);

      // Migration: Stelle sicher, dass erwartete Spalten existieren
      try {
        const ensureColumns = async (table: string, defs: { name: string; type: string; defaultValue?: string | number }[]) => {
          const checkResult = await this.db!.query(`PRAGMA table_info(${table})`);
          if (checkResult.values && checkResult.values.length > 0) {
            const colNames = (checkResult.values || []).map((col: any) => col.name);
            for (const def of defs) {
              if (!colNames.includes(def.name)) {
                const defaultClause =
                  def.defaultValue === undefined
                    ? ''
                    : ` DEFAULT ${typeof def.defaultValue === 'string' ? `'${def.defaultValue}'` : def.defaultValue}`;
                console.log(`[SQLite] Fuege Spalte ${def.name} zu ${table} hinzu`);
                await this.db!.execute(`ALTER TABLE ${table} ADD COLUMN ${def.name} ${def.type}${defaultClause}`);
              }
            }
          } else {
            console.log(`[SQLite] Tabelle ${table} existiert noch nicht`);
          }
        };

        await ensureColumns('exam_boegen', [
          { name: 'beschreibung', type: 'TEXT' },
          { name: 'schein_id', type: 'INTEGER' },
          { name: 'schein', type: 'TEXT' },
          { name: 'schein_lang', type: 'TEXT' },
          { name: 'schein_kurz', type: 'TEXT' },
          { name: 'kurs_id', type: 'INTEGER' },
          { name: 'zeitlimit_minuten', type: 'INTEGER' },
          { name: 'bestehensgrenze_prozent', type: 'INTEGER' },
          { name: 'anzahl_fragen', type: 'INTEGER' },
          { name: 'is_offiziel', type: 'INTEGER' },
          { name: 'aktiv', type: 'INTEGER', defaultValue: 1 },
          { name: 'bogen_data', type: 'TEXT' },
          { name: 'fragen', type: 'TEXT' }
        ]);

        await ensureColumns('questions', [
          { name: 'schein_id', type: 'INTEGER' }
        ]);
        await ensureColumns('downloaded_scheine', [
          { name: 'schein_id', type: 'INTEGER' },
          { name: 'question_count', type: 'INTEGER' },
          { name: 'is_complete', type: 'INTEGER' },
          { name: 'last_updated', type: 'TEXT' },
          { name: 'has_exam_content', type: 'INTEGER' },
          { name: 'exam_bogen_count', type: 'INTEGER' },
          { name: 'exam_last_updated', type: 'TEXT' }
        ]);
      } catch (err) {
        console.log('[SQLite] Konnte Schema nicht pruefen', err);
      }


      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS exam_boegen (
          id INTEGER PRIMARY KEY,
          name TEXT,
          beschreibung TEXT,
          schein_id INTEGER,
          schein_lang TEXT,
          schein TEXT,
          schein_kurz TEXT,
          kurs_id INTEGER,
          zeitlimit_minuten INTEGER,
          bestehensgrenze_prozent INTEGER,
          anzahl_fragen INTEGER,
          is_offiziel INTEGER,
          aktiv INTEGER,
          bogen_data TEXT,
          fragen TEXT
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS exam_results (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          bogen_id INTEGER,
          user_id INTEGER,
          result TEXT,
          created_at TEXT
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS categories (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          schein TEXT,
          kategorie TEXT,
          unterkategorie TEXT,
          UNIQUE(schein, kategorie, unterkategorie)
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS question_images (
          frage_id INTEGER PRIMARY KEY,
          data TEXT,
          updated_at TEXT
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS answer_images (
          frage_id INTEGER,
          buchstabe TEXT,
          data TEXT,
          updated_at TEXT,
          PRIMARY KEY (frage_id, buchstabe)
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS session_state (
          key TEXT PRIMARY KEY,
          data TEXT,
          updated_at TEXT
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS user_token (
          id INTEGER PRIMARY KEY,
          token TEXT
        )
      `);

      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS lernplattform_kurs_schulungstage (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          kurs_id INTEGER NOT NULL,
          datum TEXT NOT NULL,
          startzeit TEXT NOT NULL,
          endzeit TEXT,
          thema TEXT,
          created_at TEXT,
          updated_at TEXT
        )
      `);

      console.log('[SQLite] Tabellen angelegt!');
      this.dbStatus$.next({
        initialized: true,
        itemsCount: 0,
        lastSyncTime: Date.now()
      });
    } catch (error) {
      console.error('[SQLite] Fehler bei Initialisierung:', error);
      this.dbStatus$.next({
        initialized: false,
        itemsCount: 0,
        lastError: String(error)
      });
    }
  }

  getDBStatus(): Observable<DBStatus> {
    return this.dbStatus$.asObservable();
  }

  isReady(): boolean {
    return this.dbStatus$.getValue().initialized;
  }

  // ===== LICENSES =====

  /**
   * Speichere Scheine/Licenses direkt
   */
  async upsertLicenses(licenses: any[]): Promise<void> {
    await this.dbReadyPromise;
    if (!this.db) {
      this.logDbUnavailable('upsertLicenses');
      // Normalisiere Lizenzen auch f�r Fallback
      this.fallbackStore.licenses = licenses.map((license: any) => {
        const scheinKurz = license.schein_kurz || license.schein || license.name || '';
        const scheinLang = license.schein_lang || license.name || scheinKurz;
        const beschreibung = license.beschreibung || license.description || '';
        const kategorie = license.kategorie || license.category || '';
        return {
          ...license,
          id: license.id ?? null,
          schein: scheinKurz,
          schein_kurz: scheinKurz,
          schein_lang: scheinLang,
          name: scheinLang,
          beschreibung,
          kategorie
        };
      });
      this.saveFallback();
      return;
    }

    console.log(`[SQLite] Speichere ${licenses.length} Scheine in DB...`);

    for (const license of licenses) {
      const scheinKurz = license.schein_kurz || license.schein || license.name || '';
      const scheinLang = license.schein_lang || license.name || scheinKurz;
      const beschreibung = license.beschreibung || license.description || '';
      const kategorie = license.kategorie || license.category || '';
      await this.db.run(
        `INSERT OR REPLACE INTO licenses (id, schein, name, beschreibung, schein_kurz, schein_lang, kategorie) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [
          license.id ?? null,
          // schein (legacy) bekommt den kurzen Identifier, damit alte Zugriffe weiterhin funktionieren
          scheinKurz,
          scheinLang,
          beschreibung,
          scheinKurz,
          scheinLang,
          kategorie
        ]
      );
    }

    console.log('[SQLite] Scheine gespeichert!');
  }

  /**
   * Lade alle Scheine aus licenses-Tabelle
   */
  async getLicenses(): Promise<any[]> {
    await this.dbReadyPromise;
    if (!this.db) {
      this.logDbUnavailable('getLicenses');
      return (this.fallbackStore.licenses || []).map((lic: any) => ({
        ...lic,
        id: lic.id ?? null,
        schein_kurz: lic.schein_kurz || lic.schein || lic.name || '',
        schein_lang: lic.schein_lang || lic.name || lic.schein_kurz || '',
        name: lic.name || lic.schein_lang || lic.schein_kurz || '',
        beschreibung: lic.beschreibung || '',
        kategorie: lic.kategorie || ''
      }));
    }

    console.log('[SQLite] Lade alle verf�gbaren Scheine aus licenses-Tabelle');
    const res = await this.db.query('SELECT * FROM licenses ORDER BY COALESCE(schein_kurz, schein, name)');
    const licenses = (res.values || []).map((row: any) => {
      const scheinKurz = row.schein_kurz || row.schein || row.name || '';
      const scheinLang = row.schein_lang || row.name || scheinKurz;
      return {
        ...row,
        id: row.id ?? null,
        schein_kurz: scheinKurz,
        schein_lang: scheinLang,
        name: scheinLang,
        beschreibung: row.beschreibung || '',
        kategorie: row.kategorie || ''
      };
    });

    console.log(`[SQLite] Scheine gefunden: ${licenses.length}`);

    return licenses;
  }

  /**
   * Legacy: Extrahiere Scheine aus Fragen (Fallback wenn licenses-Tabelle leer)
   */
  async getScheine(): Promise<any[]> {
    if (!this.db) {
      this.logDbUnavailable('getScheine');
      return [];
    }

    // Erst versuchen aus licenses-Tabelle zu laden
    const licensesResult = await this.getLicenses();
    if (licensesResult.length > 0) {
      return licensesResult;
    }

    // Fallback: Aus Fragen extrahieren
    console.log('[SQLite] Fallback: Lade Scheine aus questions-Tabelle');
    const res = await this.db.query('SELECT DISTINCT schein FROM questions ORDER BY schein');
    console.log(`[SQLite] Scheine gefunden: ${res.values?.length || 0}`);

    // Konvertiere zu License-Format
    return (res.values || []).map((row: any) => ({
      schein: row.schein,
      name: row.schein,
      beschreibung: ''
    }));
  }

  async upsertScheine(scheine: any[]): Promise<void> {
    if (!this.db) {
      this.logDbUnavailable('upsertScheine');
      return;
    }
    console.log(`[SQLite] Speichere ${scheine.length} Scheine in neue scheine-Tabelle...`);
    for (const schein of scheine) {
      await this.db.run(
        `INSERT OR REPLACE INTO scheine (kategorie, schein_kurz, schein_lang, status, beschreibung) VALUES (?, ?, ?, ?, ?)`,
        [
          schein.kategorie || '',
          schein.schein_kurz || schein.schein || '',
          schein.schein_lang || schein.name || '',
          typeof schein.status === 'number' ? schein.status : 1,
          schein.beschreibung || schein.description || ''
        ]
      );
    }
    console.log('[SQLite] Scheine gespeichert!');
  }

  async getQuestions(schein?: string, kategorie?: string, unterkategorie?: string): Promise<Question[]> {
    if (!this.db) {
      this.logDbUnavailable('getQuestions');
      const grouped: any = this.fallbackStore.questions || {};
      const groupedById: any = this.fallbackStore.questionsById || {};
      let qs: any[] = [];
      const numeric = schein ? Number(schein) : NaN;
      if (schein) {
        if (!Number.isNaN(numeric) && groupedById[numeric]) {
          qs = groupedById[numeric];
        } else if (grouped[schein]) {
          qs = grouped[schein];
        }
      }
      if (qs.length === 0) {
        // wenn kein spezifischer Treffer, alle zusammensammeln
        qs = Object.values(grouped).flat();
      }
      const filtered = qs.filter(q => {
        if (kategorie && q.kategorie !== kategorie) return false;
        if (unterkategorie && q.unterkategorie !== unterkategorie) return false;
        if (schein && q.schein !== schein) return false;
        return true;
      });
      console.log(`[SQLite] getQuestions (fallback): ${filtered.length}`);
      return filtered;
    }

    let query = 'SELECT * FROM questions';
    let params: any[] = [];
    let conditions: string[] = [];

    if (schein) {
      conditions.push('schein = ?');
      params.push(schein);
    }

    // Falls mit schein_id gefiltert werden soll (kann �ber schein als Zahl kommen)
    const numericSchein = schein ? Number(schein) : NaN;
    if (!Number.isNaN(numericSchein)) {
      conditions.push('(schein_id = ? OR schein = ?)');
      params.push(numericSchein);
      params.push(schein);
    }

    if (kategorie) {
      conditions.push('kategorie = ?');
      params.push(kategorie);
    }

    if (unterkategorie) {
      conditions.push('unterkategorie = ?');
      params.push(unterkategorie);
    }

    if (conditions.length > 0) {
      query += ' WHERE ' + conditions.join(' AND ');
    }

    console.log(`[SQLite] Lade Fragen: schein=${schein}, kategorie=${kategorie}, unterkategorie=${unterkategorie}`);
    console.log(`[SQLite] Query: ${query}, Params: ${JSON.stringify(params)}`);

    const res = await this.db.query(query, params);

    console.log(`[SQLite] Fragen geladen: ${res.values?.length || 0}`);

    // Debug: Wenn keine Fragen gefunden, zeige alle vorhandenen Schein-Werte
    if (schein && (!res.values || res.values.length === 0)) {
      const allScheineRes = await this.db.query('SELECT DISTINCT schein FROM questions LIMIT 10');
      console.log(`[SQLite] DEBUG: Vorhandene Scheine in DB:`, allScheineRes.values?.map((r: any) => r.schein) || []);
    }
    const mapped = (res.values || []).map((row: QuestionRow) => ({
      frage_id: row.frage_id,
      schein_id: row.schein_id ?? null,
      schein: row.schein,
      kategorie: row.kategorie,
      unterkategorie: row.unterkategorie,
      buchseite: row.buchseite,
      frage_text: row.frage_text,
      korrekte_antwort: row.korrekte_antwort,
      has_image: !!row.has_image,
      off_fragen_id: row.off_fragen_id,
      antworten: JSON.parse(row.antworten)
    }));
    // auch im Fallback speichern
    if (schein) {
      const grouped: any = this.fallbackStore.questions || {};
      grouped[schein] = mapped;
      const groupedById: any = this.fallbackStore.questionsById || {};
      const targetId = mapped[0]?.schein_id ?? null;
      if (targetId !== null) {
        groupedById[targetId] = mapped;
      }
      this.fallbackStore.questions = grouped;
      this.fallbackStore.questionsById = groupedById;
      this.saveFallback();
    }
    return mapped;
  }

  async getQuestion(frageId: number): Promise<Question | null> {
    if (!this.db) {
      this.logDbUnavailable('getQuestion');
      return null;
    }
    console.log(`[SQLite] Lade Frage: ${frageId}`);
    const res = await this.db.query('SELECT * FROM questions WHERE frage_id = ?', [frageId]);
    if (!res.values || res.values.length === 0) {
      console.log('[SQLite] Frage nicht gefunden!');
      return null;
    }
    const row: QuestionRow = res.values[0];
    return {
      frage_id: row.frage_id,
      schein_id: row.schein_id ?? null,
      schein: row.schein,
      kategorie: row.kategorie,
      unterkategorie: row.unterkategorie,
      buchseite: row.buchseite,
      frage_text: row.frage_text,
      korrekte_antwort: row.korrekte_antwort,
      has_image: !!row.has_image,
      off_fragen_id: row.off_fragen_id,
      antworten: JSON.parse(row.antworten)
    };
  }

  private selectedScheinIdFromValue(value: string): number | null {
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  async getQuestionsChangedSince(timestamp: number): Promise<Question[]> {
    return [];
  }

  async upsertQuestions(questions: Question[]): Promise<void> {
    if (!this.db) {
      this.logDbUnavailable('upsertQuestions');
      return;
    }
    console.log(`[SQLite] Speichere ${questions.length} Fragen in DB...`);

    // Debug: Zeige die Schein-Werte der ersten Fragen
    if (questions.length > 0) {
      const firstQuestion = questions[0];
      console.log(`[SQLite] DEBUG: Erste Frage komplett:`, JSON.stringify(firstQuestion).substring(0, 200));
      console.log(`[SQLite] DEBUG: Erste Frage .schein:`, firstQuestion.schein);
      console.log(`[SQLite] DEBUG: Erste Frage keys:`, Object.keys(firstQuestion));

      const uniqueScheine = [...new Set(questions.slice(0, 5).map(q => q.schein))];
      console.log(`[SQLite] DEBUG: Schein-Werte in ersten Fragen:`, uniqueScheine);
    }

    for (const q of questions) {
      await this.db.run(
        'INSERT OR REPLACE INTO questions (frage_id, schein_id, schein, kategorie, unterkategorie, buchseite, frage_text, korrekte_antwort, has_image, off_fragen_id, antworten) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)',
        [
          q.frage_id,
          q.schein_id ?? null,
          q.schein,
          q.kategorie,
          q.unterkategorie,
          q.buchseite,
          q.frage_text,
          q.korrekte_antwort,
          q.has_image ? 1 : 0,
          q.off_fragen_id || null,
          JSON.stringify(q.antworten)
        ]
      );
    }
    console.log('[SQLite] Fragen gespeichert!');
  }

  async getProgress(userId: number, frageId: number): Promise<ProgressRecord | null> {
    if (!this.db) {
      this.logDbUnavailable('getProgress');
      return null;
    }
    console.log(`[SQLite] Lade Progress f�r Frage ${frageId}, User ${userId}`);
    const res = await this.db.query(
      'SELECT * FROM progress WHERE user_id = ? AND frage_id = ?',
      [userId, frageId]
    );
    console.log(`[SQLite] Progress geladen: ${res.values?.length || 0}`);
    return res.values && res.values.length > 0 ? res.values[0] : null;
  }

  async getUserProgress(userId: number): Promise<ProgressRecord[]> {
    if (!this.db) {
      this.logDbUnavailable('getUserProgress');
      return [];
    }
    console.log(`[SQLite] Lade Progress f�r User ${userId}`);
    const res = await this.db.query('SELECT * FROM progress WHERE user_id = ?', [userId]);
    console.log(`[SQLite] Progress geladen: ${res.values?.length || 0}`);
    return res.values || [];
  }

  async getSyncPendingProgress(): Promise<ProgressRecord[]> {
    return [];
  }

  async saveProgress(progress: ProgressRecord): Promise<void> {
    if (!this.db) {
      this.logDbUnavailable('saveProgress');
      return;
    }
    console.log(`[SQLite] Speichere Progress f�r Frage ${progress.frage_id}, User ${progress.user_id}`);
    await this.db.run(
      'INSERT OR REPLACE INTO progress (frage_id, user_id, antwort, ist_richtig, zeit_sekunden, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
      [progress.frage_id, progress.user_id, progress.antwort, progress.ist_richtig, progress.zeit_sekunden, progress.updated_at]
    );
    console.log('[SQLite] Progress gespeichert!');
  }

  async markProgressSynced(progressIds: number[]): Promise<void> {
    console.log(`Marking ${progressIds.length} progress items as synced`);
  }

    async getExamBoegen(): Promise<any[]> {
    await this.dbReadyPromise;
    if (!this.db) {
      this.logDbUnavailable('getExamBoegen');
      return [];
    }
    console.log('[SQLite] Lade Exam-Boegen');
    const res = await this.db.query('SELECT * FROM exam_boegen');
    console.log(`[SQLite] Exam-Boegen geladen: ${res.values?.length || 0}`);
    if (res.values && res.values.length > 0) {
      const sample = res.values[0];
      console.log('[ExamDbg][DB] exam_boegen first row=', JSON.stringify({
        id: sample.id,
        name: sample.name,
        schein: sample.schein,
        schein_kurz: sample.schein_kurz,
        schein_lang: sample.schein_lang,
        kurs_id: sample.kurs_id
      }));
    }
      return (res.values || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        beschreibung: row.beschreibung || '',
        schein_id: row.schein_id ?? null,
        schein: row.schein_lang || row.schein || row.schein_kurz || '',
        schein_lang: row.schein_lang || row.schein || row.schein_kurz || '',
        schein_kurz: row.schein_kurz || row.schein || '',
      kurs_id: row.kurs_id || null,
      zeitlimit_minuten: row.zeitlimit_minuten || 60,
      bestehensgrenze_prozent: row.bestehensgrenze_prozent || 80,
      anzahl_fragen: row.anzahl_fragen || 0,
      is_offiziel: row.is_offiziel || 0
    }));
  }

  async upsertExamBoegen(boegen: any[]): Promise<void> {
    await this.dbReadyPromise;
    if (!this.db) {
      this.logDbUnavailable('upsertExamBoegen');
      return;
    }
    console.log(`[SQLite] Speichere ${boegen.length} Exam-Boegen`);
    if (boegen.length > 0) {
      const sample = boegen[0];
      console.log('[ExamDbg][DB][UPSERT] sample incoming=', JSON.stringify({
        id: sample.id,
        name: sample.name,
        schein: sample.schein,
        schein_kurz: sample.schein_kurz,
        schein_lang: sample.schein_lang,
        kurs_id: sample.kurs_id
      }));
    }
    for (const b of boegen) {
      if (!b.schein && !b.schein_kurz && !b.schein_lang) {
        console.warn('[ExamDbg][DB][UPSERT] missing schein fields for id', b.id, 'data=', JSON.stringify(b));
      }
      await this.db.run(
        `INSERT OR REPLACE INTO exam_boegen
         (id, name, beschreibung, schein_id, schein_lang, schein, schein_kurz, kurs_id, zeitlimit_minuten, bestehensgrenze_prozent,
          anzahl_fragen, is_offiziel, aktiv, bogen_data, fragen)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          b.id,
          b.name,
          b.beschreibung || '',
          b.schein_id ?? null,
          b.schein_lang || b.schein || b.schein_kurz || null,
          b.schein || b.schein_kurz || null,
          b.schein_kurz || b.schein || null,
          b.kurs_id || null,
          b.zeitlimit_minuten || 60,
          b.bestehensgrenze_prozent || 80,
          b.anzahl_fragen || 0,
          b.is_offiziel || 0,
          1,
          JSON.stringify(b),
          JSON.stringify(b.fragen || [])
        ]
      );
    }
    console.log('[SQLite] Exam-Boegen gespeichert!');
  }
async getExamWithQuestions(bogenId: number): Promise<any | null> {
    if (!this.db) {
      this.logDbUnavailable('getExamWithQuestions');
      return null;
    }

    try {
      const res = await this.db.query('SELECT * FROM exam_boegen WHERE id = ?', [bogenId]);

      if (!res.values || res.values.length === 0) {
        console.error(`[SQLite] Exam-Bogen ${bogenId} nicht gefunden!`);
        return null;
      }

      const row = res.values[0];
      const fragen = JSON.parse(row.fragen || '[]');

      return {
        bogen: {
          id: row.id,
          name: row.name,
          beschreibung: row.beschreibung || '',
          schein_id: row.schein_id ?? null,
          schein: row.schein_lang || row.schein || '',
          kurs_id: row.kurs_id || null,
          zeitlimit_minuten: row.zeitlimit_minuten || 60,
          bestehensgrenze_prozent: row.bestehensgrenze_prozent || 80,
          aktiv: row.aktiv || 1
        },
        fragen: fragen,
        anzahl_fragen: fragen.length
      };
    } catch (error) {
      console.error(`[SQLite] ERROR in getExamWithQuestions:`, error);
      return null;
    }
  }

  async saveExamResult(result: any): Promise<void> {
    if (!this.db) {
      this.logDbUnavailable('saveExamResult');
      return;
    }
    console.log('[SQLite] Speichere Exam-Result');
    await this.db.run(
      'INSERT INTO exam_results (bogen_id, user_id, result, created_at) VALUES (?, ?, ?, ?)',
      [result.bogen_id, result.user_id, JSON.stringify(result), result.created_at]
    );
    console.log('[SQLite] Exam-Result gespeichert!');
  }

  async getExamResults(userId?: number): Promise<any[]> {
    if (!this.db) {
      this.logDbUnavailable('getExamResults');
      return [];
    }
    if (userId) {
      console.log(`[SQLite] Lade Exam-Results f�r User ${userId}`);
      const res = await this.db.query('SELECT * FROM exam_results WHERE user_id = ?', [userId]);
      console.log(`[SQLite] Exam-Results geladen: ${res.values?.length || 0}`);
      return (res.values || []).map((row: any) => ({
        id: row.id,
        bogen_id: row.bogen_id,
        user_id: row.user_id,
        result: JSON.parse(row.result),
        created_at: row.created_at
      }));
    } else {
      console.log('[SQLite] Lade alle Exam-Results');
      const res = await this.db.query('SELECT * FROM exam_results');
      console.log(`[SQLite] Exam-Results geladen: ${res.values?.length || 0}`);
      return (res.values || []).map((row: any) => ({
        id: row.id,
        bogen_id: row.bogen_id,
        user_id: row.user_id,
        result: JSON.parse(row.result),
        created_at: row.created_at
      }));
    }
  }

  async getKursSchulungstage(kursId: number): Promise<any[]> {
    if (!this.db) {
      this.logDbUnavailable('getKursSchulungstage');
      return [];
    }
    console.log(`[SQLite] Lade Schulungstage f\u00fcr Kurs ${kursId}`);
    const res = await this.db.query(
      'SELECT * FROM lernplattform_kurs_schulungstage WHERE kurs_id = ? ORDER BY datum, startzeit',
      [kursId]
    );
    return (res.values || []).map((row: any) => ({
      id: row.id,
      kurs_id: row.kurs_id,
      datum: row.datum,
      startzeit: row.startzeit,
      endzeit: row.endzeit,
      thema: row.thema
    }));
  }

  async upsertKursSchulungstage(kursId: number, entries: any[]): Promise<void> {
    await this.dbReadyPromise;
    if (!this.db) {
      this.logDbUnavailable('upsertKursSchulungstage');
      return;
    }
    console.log(`[SQLite] Speichere ${entries?.length || 0} Schulungstage f\u00fcr Kurs ${kursId}`);
    await this.db.run('DELETE FROM lernplattform_kurs_schulungstage WHERE kurs_id = ?', [kursId]);
    const now = new Date().toISOString();
    for (const entry of entries || []) {
      await this.db.run(
        'INSERT INTO lernplattform_kurs_schulungstage (kurs_id, datum, startzeit, endzeit, thema, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?)',
        [
          kursId,
          entry.datum,
          entry.startzeit,
          entry.endzeit ?? null,
          entry.thema ?? null,
          entry.created_at ?? now,
          entry.updated_at ?? now
        ]
      );
    }
  }

  async saveUser(user: User): Promise<void> {
    console.log(`Saving user: ${user.vorname} ${user.nachname}`);
  }

  async getUser(): Promise<User | null> {
    return null;
  }

  async getStatistics(): Promise<{
    questionsCount: number;
    progressCount: number;
    examResultsCount: number;
    totalSize: number;
  }> {
    if (!this.db) {
      return {
        questionsCount: 0,
        progressCount: 0,
        examResultsCount: 0,
        totalSize: 0
      };
    }
    const questionsRes = await this.db.query('SELECT COUNT(*) as count FROM questions');
    const progressRes = await this.db.query('SELECT COUNT(*) as count FROM progress');
    const examResultsRes = await this.db.query('SELECT COUNT(*) as count FROM exam_results');

    return {
      questionsCount: questionsRes.values?.[0]?.count || 0,
      progressCount: progressRes.values?.[0]?.count || 0,
      examResultsCount: examResultsRes.values?.[0]?.count || 0,
      totalSize: 0
    };
  }

  async clear(): Promise<void> {
    if (!this.db) {
      this.logDbUnavailable('clear');
      return;
    }
    console.log('[SQLite] Clearing user data from database');
    await this.db.execute('DELETE FROM progress');
    await this.db.execute('DELETE FROM exam_results');
    await this.db.execute('DELETE FROM user_token');
  }

  async clearAll(): Promise<void> {
    if (!this.db) {
      this.logDbUnavailable('clearAll');
      return;
    }
    console.log('[SQLite] Clearing entire database');
    await this.db.execute('DELETE FROM questions');
    await this.db.execute('DELETE FROM progress');
    await this.db.execute('DELETE FROM exam_boegen');
    await this.db.execute('DELETE FROM exam_results');
    await this.db.execute('DELETE FROM user_token');
    await this.db.execute('DELETE FROM downloaded_scheine');
    await this.db.execute('DELETE FROM categories');
  }

  async exportDatabase(): Promise<Blob> {
    return new Blob(['Database export stub']);
  }

  async getSchemaVersion(): Promise<number> {
    return 1;
  }

  async saveToken(token: string): Promise<void> {
    await this.dbReadyPromise;
    if (!this.db) {
      this.logDbUnavailable('saveToken');
      return;
    }
    console.log('[SQLite] Speichere Token');
    await this.db.run('INSERT OR REPLACE INTO user_token (id, token) VALUES (1, ?)', [token]);
    console.log('[SQLite] Token gespeichert!');
  }

  async getToken(): Promise<string | null> {
    await this.dbReadyPromise;
    if (!this.db) {
      this.logDbUnavailable('getToken');
      return null;
    }
    console.log('[SQLite] Lade Token');
    const res = await this.db.query('SELECT token FROM user_token WHERE id = 1');
    const token = res.values && res.values.length > 0 ? res.values[0].token : null;
    console.log(`[SQLite] Token geladen: ${token ? 'vorhanden' : 'nicht vorhanden'}`);
    return token;
  }

  async clearToken(): Promise<void> {
    await this.dbReadyPromise;
    if (!this.db) {
      this.logDbUnavailable('clearToken');
      return;
    }
    console.log('[SQLite] Token l�schen');
    await this.db.run('DELETE FROM user_token WHERE id = 1');
  }

  async isUserLoggedIn(): Promise<boolean> {
    const token = await this.getToken();
    return !!token && token.length > 10;
  }

  async syncToServer(apiService: any): Promise<void> {
    if (!navigator.onLine) {
      console.log('[SQLite] Offline - Sync �bersprungen');
      return;
    }
    console.log('[SQLite] Starte Sync zum Server...');

    const progressRes = await this.db?.query('SELECT * FROM progress');
    const allProgress = progressRes?.values || [];
    console.log(`[SQLite] Synchronisiere ${allProgress.length} Progress-Eintr�ge`);
    for (const p of allProgress) {
      try {
        await apiService.post('progress/sync.php', p);
      } catch (error) {
        console.error('[SQLite] Fehler beim Sync von Progress:', error);
      }
    }

    const examRes = await this.db?.query('SELECT * FROM exam_results');
    const allExams = examRes?.values || [];
    console.log(`[SQLite] Synchronisiere ${allExams.length} Exam-Results`);
    for (const e of allExams) {
      try {
        await apiService.post('exam/sync.php', e);
      } catch (error) {
        console.error('[SQLite] Fehler beim Sync von Exam-Result:', error);
      }
    }

    this.dbStatus$.next({
      ...this.dbStatus$.getValue(),
      lastSyncTime: Date.now()
    });
    console.log('[SQLite] Sync abgeschlossen!');
  }

  // ===== CATEGORIES =====

  /**
   * Speichere Kategorien f�r einen Schein
   */
  async upsertCategories(schein: string, categories: any[]): Promise<void> {
    await this.dbReadyPromise;
    if (!this.db) {
      if (!this.fallbackStore.categories) this.fallbackStore.categories = {};
      this.fallbackStore.categories[schein] = categories;
      this.saveFallback();
      console.warn('[SQLite] upsertCategories: fallback gespeichert');
      return;
    }

    console.log(`[SQLite] Speichere ${categories.length} Kategorien f�r ${schein}...`);

    for (const cat of categories) {
      // Kategorien haben die Struktur: { kategorie: "X", unterkategorien: [{unterkategorie: "Y"}] }
      const katName = cat.kategorie || cat.name || '';

      // Wenn es Unterkategorien gibt, speichere jede einzeln
      if (cat.unterkategorien && Array.isArray(cat.unterkategorien) && cat.unterkategorien.length > 0) {
        for (const subcat of cat.unterkategorien) {
          await this.db.run(
            `INSERT OR REPLACE INTO categories (schein, kategorie, unterkategorie) VALUES (?, ?, ?)`,
            [schein, katName, subcat.unterkategorie || subcat.name || '']
          );
        }
      } else {
        // Keine Unterkategorien - speichere nur die Hauptkategorie
        await this.db.run(
          `INSERT OR REPLACE INTO categories (schein, kategorie, unterkategorie) VALUES (?, ?, ?)`,
          [schein, katName, '']
        );
      }
    }

    console.log('[SQLite] Kategorien gespeichert!');
  }

  /**
   * Lade alle Kategorien f�r einen Schein
   */
  async getCategories(schein: string): Promise<any[]> {
    await this.dbReadyPromise;
    if (!this.db) {
      console.warn('[SQLite] getCategories: keine DB � return []');
      return [];
    }

    console.log(`[SQLite] Lade Kategorien f�r Schein (schein_kurz): ${schein}`);

    const result = await this.db.query(
      `SELECT DISTINCT kategorie, unterkategorie FROM categories WHERE schein = ?`,
      [schein]
    );

    const rows = result.values || [];
    console.log(`[SQLite] Kategorien-Rows gefunden: ${rows.length}`);

    // Gruppiere nach Kategorie und erstelle das API-Format
    const categoryMap = new Map<string, any>();

    for (const row of rows) {
      const katName = row.kategorie || '';

      if (!categoryMap.has(katName)) {
        categoryMap.set(katName, {
          kategorie: katName,
          unterkategorien: []
        });
      }

      if (row.unterkategorie) {
        categoryMap.get(katName)!.unterkategorien.push({
          unterkategorie: row.unterkategorie,
          fragen_anzahl: 0 // Wird bei Bedarf berechnet
        });
      }
    }

    const categories = Array.from(categoryMap.values());
    console.log(`[SQLite] Kategorien gefunden: ${categories.length}`);

    return categories;
  }

  // ===== DOWNLOADED SCHEINE TRACKING =====

  /**
   * Markiere einen Schein als vollst�ndig heruntergeladen
   */
  async markScheinAsDownloaded(schein: string, questionCount: number, scheinId?: number | null): Promise<void> {
      await this.dbReadyPromise;
      await this.dbReadyPromise;
      await this.dbReadyPromise;
      await this.dbReadyPromise;
    if (!this.db) {
      console.warn('[SQLite] markScheinAsDownloaded: keine DB - skippe offline state');
      if (!this.fallbackStore.downloadedScheine) this.fallbackStore.downloadedScheine = [];
      if (!this.fallbackStore.downloadedScheineIds) this.fallbackStore.downloadedScheineIds = [];
      if (!this.fallbackStore.downloadedScheine.includes(schein)) {
        this.fallbackStore.downloadedScheine.push(schein);
        this.fallbackStore.downloadedScheineIds.push(scheinId ?? null);
        this.saveFallback();
      }
      return;
    }

    // Wenn keine ID mitgegeben wurde, versuche sie aus licenses zu lesen
    if (scheinId === undefined) {
      const idLookup = await this.db.query('SELECT id FROM licenses WHERE schein = ? OR schein_kurz = ? OR name = ?', [schein, schein, schein]);
      const row = idLookup.values?.[0];
      scheinId = row?.id ?? null;
    }

    console.log(`[SQLite] Markiere ${schein} als heruntergeladen (${questionCount} Fragen)`);

    const now = new Date().toISOString();
    await this.db.run(
      `INSERT OR REPLACE INTO downloaded_scheine (schein, schein_id, download_date, question_count, is_complete, last_updated)
       VALUES (?, ?, ?, ?, 1, ?)`,
      [schein, scheinId ?? null, now, questionCount, now]
    );

    if (!this.fallbackStore.downloadedScheine.includes(schein)) {
      this.fallbackStore.downloadedScheine.push(schein);
      if (!this.fallbackStore.downloadedScheineIds) this.fallbackStore.downloadedScheineIds = [];
      this.fallbackStore.downloadedScheineIds.push(scheinId ?? null);
      this.saveFallback();
    }

    console.log('[SQLite] Schein als heruntergeladen markiert!');
  }

  /**
   * Pr�fe ob ein Schein vollst�ndig offline verf�gbar ist
   */
  async isScheinAvailableOffline(schein: string): Promise<boolean> {
    if (!this.db) {
      this.logDbUnavailable('isScheinAvailableOffline');
      return false;
    }

    const result = await this.db.query(
      `SELECT is_complete FROM downloaded_scheine WHERE schein = ?`,
      [schein]
    );

    const isAvailable = !!(result.values && result.values.length > 0 && result.values[0].is_complete === 1);
    console.log(`[SQLite] Schein ${schein} offline verf�gbar: ${isAvailable}`);
    return isAvailable;
  }

  /**
   * Hole alle vollst�ndig heruntergeladenen Scheine
   */
  async getOfflineScheine(): Promise<string[]> {
    if (!this.db) {
      this.logDbUnavailable('getOfflineScheine');
      if (!this.fallbackStore.downloadedScheineIds) this.fallbackStore.downloadedScheineIds = [];
      return this.fallbackStore.downloadedScheine || [];
    }

    const result = await this.db.query(
      `SELECT schein, schein_id FROM downloaded_scheine WHERE is_complete = 1 ORDER BY schein`
    );

    const scheine = (result.values || []).map((row: any) => row.schein);
    const scheinIds = (result.values || []).map((row: any) => row.schein_id ?? null);
    this.fallbackStore.downloadedScheine = scheine;
    this.fallbackStore.downloadedScheineIds = scheinIds;
    this.saveFallback();
    console.log(`[SQLite] Offline Scheine gefunden: ${scheine.length}`, scheine);
    return scheine;
  }

  /**
   * Hole heruntergeladene Scheine mit IDs (optional fǬr sp��ter Mapping)
   */
  async getOfflineScheineWithIds(): Promise<{ schein: string; schein_id: number | null }[]> {
    if (!this.db) {
      const names = this.fallbackStore.downloadedScheine || [];
      const ids = this.fallbackStore.downloadedScheineIds || [];
      return names.map((s: string, idx: number) => ({ schein: s, schein_id: ids[idx] ?? null }));
    }

    const result = await this.db.query(
      `SELECT schein, schein_id FROM downloaded_scheine WHERE is_complete = 1 ORDER BY schein`
    );
    return (result.values || []).map((row: any) => ({
      schein: row.schein,
      schein_id: row.schein_id ?? null
    }));
  }

  /**
   * Hole Download-Info f�r einen Schein
   */
  async getScheinDownloadInfo(schein: string): Promise<{questionCount: number, downloadDate: string} | null> {
    if (!this.db) {
      this.logDbUnavailable('getScheinDownloadInfo');
      return null;
    }

    const result = await this.db.query(
      `SELECT question_count, download_date FROM downloaded_scheine WHERE schein = ?`,
      [schein]
    );

    if (!result.values || result.values.length === 0) {
      return null;
    }

    const row = result.values[0];
    return {
      questionCount: row.question_count || 0,
      downloadDate: row.download_date
    };
  }

  /**
   * Entferne einen Schein aus der Offline-Tracking-Tabelle
   * (L�scht NICHT die Fragen selbst, nur den Download-Status)
   */
  async removeOfflineSchein(schein: string): Promise<void> {
    if (!this.db) {
      console.warn('[SQLite] removeOfflineSchein: keine DB - skippe offline state');
      if (this.fallbackStore.downloadedScheine) {
        const idx = this.fallbackStore.downloadedScheine.indexOf(schein);
        if (idx >= 0) {
          this.fallbackStore.downloadedScheine.splice(idx, 1);
          if (this.fallbackStore.downloadedScheineIds && this.fallbackStore.downloadedScheineIds.length > idx) {
            this.fallbackStore.downloadedScheineIds.splice(idx, 1);
          }
          this.saveFallback();
        }
      }
      return;
    }

    console.log(`[SQLite] Entferne Schein ${schein} aus Offline-Tracking`);
    await this.db.run(`DELETE FROM downloaded_scheine WHERE schein = ?`, [schein]);
    console.log('[SQLite] Schein aus Tracking entfernt!');
  }

  // ===== EXAM CONTENT TRACKING =====

  /**
   * Markiere einen Schein als vollst�ndig mit Exam-B�gen heruntergeladen
   */
  async markScheinExamsAsDownloaded(schein: string, bogenCount: number, scheinId?: number | null): Promise<void> {
    await this.dbReadyPromise;
    if (!this.db) {
      console.warn('[SQLite] markScheinExamsAsDownloaded: keine DB � skippe offline state');
      return;
    }

    console.log(`[SQLite] Markiere Exam-B�gen f�r ${schein} als heruntergeladen (${bogenCount} B�gen)`);

    const now = new Date().toISOString();

    // Insert or update: Setze has_exam_content = 1
    await this.db.run(
      `INSERT INTO downloaded_scheine (schein, schein_id, download_date, has_exam_content, exam_bogen_count, exam_last_updated)
       VALUES (?, ?, ?, 1, ?, ?)
       ON CONFLICT(schein) DO UPDATE SET
         schein_id = COALESCE(excluded.schein_id, downloaded_scheine.schein_id),
         has_exam_content = 1,
         exam_bogen_count = ?,
         exam_last_updated = ?`,
      [schein, now, bogenCount, now, bogenCount, now]
    );

    console.log('[SQLite] Exam-B�gen als heruntergeladen markiert!');
  }

  /**
   * Pr�fe ob Exam-B�gen f�r einen Schein offline verf�gbar sind
   */
  async isScheinExamsAvailableOffline(schein: string): Promise<boolean> {
    if (!this.db) {
      this.logDbUnavailable('isScheinExamsAvailableOffline');
      return false;
    }

    const result = await this.db.query(
      `SELECT has_exam_content FROM downloaded_scheine WHERE schein = ?`,
      [schein]
    );

    const isAvailable = !!(result.values && result.values.length > 0 && result.values[0].has_exam_content === 1);
    console.log(`[SQLite] Exam-B�gen f�r ${schein} offline verf�gbar: ${isAvailable}`);
    return isAvailable;
  }

  /**
   * Hole alle Scheine mit offline verf�gbaren Exam-B�gen
   */
  async getOfflineScheineExams(): Promise<string[]> {
    if (!this.db) {
      this.logDbUnavailable('getOfflineScheineExams');
      return [];
    }

    const result = await this.db.query(
      `SELECT schein FROM downloaded_scheine WHERE has_exam_content = 1 ORDER BY schein`
    );

    const scheine = (result.values || []).map((row: any) => row.schein);
    console.log(`[SQLite] Scheine mit Exam-B�gen offline: ${scheine.length}`, scheine);
    return scheine;
  }

  /**
   * Hole Exam-Download-Info f�r einen Schein
   */
  async getScheinExamDownloadInfo(schein: string): Promise<{bogenCount: number, downloadDate: string} | null> {
    if (!this.db) {
      this.logDbUnavailable('getScheinExamDownloadInfo');
      return null;
    }

    const result = await this.db.query(
      `SELECT exam_bogen_count, exam_last_updated FROM downloaded_scheine WHERE schein = ?`,
      [schein]
    );

    if (!result.values || result.values.length === 0) {
      return null;
    }

    const row = result.values[0];
    return {
      bogenCount: row.exam_bogen_count || 0,
      downloadDate: row.exam_last_updated
    };
  }

  public setupSyncOnReconnect(apiService: any): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('online', () => {
      console.log('[SQLite] Online-Event - starte Sync');
      this.syncToServer(apiService);
    });
  }
}


